import { useLayoutEffect, useRef, useState } from 'react';
import { MdClose } from 'react-icons/md';
import Modal from 'components/Modal';
import { PlaceholderText } from 'components/Placeholders';
import SoftButton from 'components/Button/SoftButton';
import Stack from 'components/Stack';
import styled from 'styled-components';

const DescriptionParagraph = styled.p`
    color: ${({ theme }) => theme.gray_2};
    font-family: ${({ theme }) => theme.style_primary};
    font-size: ${({ theme }) => theme.fontSize_base};
    line-height: 1.125em;
    margin-top: 0;
    padding: 0;
`;

const TruncatedDescriptionWrapper = styled.div`
    display: grid;
    max-width: 38rem;
    grid-template-columns: auto minmax(max-content, 1fr);
    align-items: baseline;
`;

const DescriptionModalHeader = styled.header`
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: solid 1px ${({ theme }) => theme.gray_5};
    padding: ${({ theme }) => `${theme.space_7}px ${theme.space_9}px`};
`;

const EnsureFirstLineNotCutOffIfFits = styled.span`
    display: inline-block;
    visibility: hidden;
    min-height: 1px;
    min-width: 1rem;
`;

const ModalTitle = styled.h2`
    font-size: 1rem;
    font-weight: ${({ theme }) => theme.fontBold};
    color: ${({ theme }) => theme.gray_1};
    margin: 0;
    text-transform: capitalize;
`;

const DescriptionModalParagraph = styled(DescriptionParagraph)`
    color: ${({ theme }) => theme.gray_1};
    line-height: 1.5em;
`;

const WatchlistDescriptionWrapper = styled.div`
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-all;
`;

const CloseBtn = styled(SoftButton).attrs(({ IconLeft = MdClose }) => ({
    IconLeft,
}))``;

const DescriptionModal = styled.aside`
    position: relative;
    z-index: 1;
    width: 34rem;
    max-width: calc(100vw - ${({ theme }) => theme.space_8 * 2}px);
    max-height: calc(100vh - ${({ theme }) => theme.space_8 * 2}px);
    background-color: ${({ theme }) => theme.gray_7};
    border-radius: ${({ theme }) => theme.borderRadius_lg};
    box-shadow: 0px 3px 7px rgba(0, 0, 0, 0.12);
    font-family: ${({ theme }) => theme.style_primary};
    font-size: ${({ theme }) => theme.fontSize_base};
    display: flex;
    flex-direction: column;
    overflow: hidden;
`;

const splitParagraphs = (text) => text.split(/[\r\n]+/gm);

function WatchlistDescription({ isLoading, description, title }) {
    const ref = useRef();
    const [showMoreButton, setShowMoreButton] = useState(false);
    const [showDescriptionModal, setShowDescriptionModal] = useState(false);

    const paragraphs = description ? splitParagraphs(description) : null;

    useLayoutEffect(() => {
        const isOverflow =
            ref.current && ref.current.scrollHeight > ref.current.clientHeight;
        if (isOverflow && !showMoreButton) {
            setShowMoreButton(true);
        } else if (!isOverflow && showMoreButton) {
            setShowMoreButton(false);
        }
    });

    return (
        <TruncatedDescriptionWrapper>
            <WatchlistDescriptionWrapper ref={ref}>
                {isLoading ? (
                    <DescriptionParagraph>
                        <PlaceholderText width="15rem" />
                    </DescriptionParagraph>
                ) : paragraphs && paragraphs.length ? (
                    paragraphs.slice(0, 2).map((paragraph, idx) => {
                        if (idx > 0) {
                            // prevent container width growing from hidden content but ensure overflow
                            return (
                                <DescriptionParagraph
                                    key={idx}
                                    aria-hidden="true"
                                >
                                    .
                                </DescriptionParagraph>
                            );
                        }
                        return (
                            <DescriptionParagraph key={idx}>
                                {paragraph}
                                {paragraphs.length > 1 ? (
                                    <EnsureFirstLineNotCutOffIfFits />
                                ) : null}
                            </DescriptionParagraph>
                        );
                    })
                ) : null}
            </WatchlistDescriptionWrapper>
            <div>
                {showMoreButton ? (
                    <SoftButton
                        size="sm"
                        onClick={() => setShowDescriptionModal(true)}
                    >
                        Read More
                    </SoftButton>
                ) : null}
            </div>
            <Modal
                open={showDescriptionModal}
                onClose={() => setShowDescriptionModal(false)}
                ModalComponent={DescriptionModal}
            >
                <DescriptionModalHeader>
                    <ModalTitle>{title}</ModalTitle>
                    <div>
                        <CloseBtn
                            onClick={() => setShowDescriptionModal(false)}
                        />
                    </div>
                </DescriptionModalHeader>
                <Stack
                    $space="1.5rem"
                    css={`
                        overflow-y: auto;
                        padding: ${({ theme }) => theme.space_9}px;
                    `}
                >
                    {paragraphs
                        ? paragraphs.map((paragraph, idx) => (
                              <DescriptionModalParagraph
                                  key={`${paragraph}-${idx}`}
                              >
                                  {paragraph}
                              </DescriptionModalParagraph>
                          ))
                        : null}
                </Stack>
            </Modal>
        </TruncatedDescriptionWrapper>
    );
}

export default WatchlistDescription;
